import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axiosInstance";
import { BASE_URL } from "../../utils/baseURL";
import { setAlert } from "./alert.slice";
import Cookies from 'js-cookie';

const initialState = {
    products: [],
    success: false,
    message: '',
    loading: false,
    error: null,
    deleteStatus: false
};

const handleErrors = (error, dispatch, rejectWithValue) => {
    const errorMessage = error.response?.data?.message || 'An error occurred';
    dispatch(setAlert({ text: errorMessage, color: 'error' }));
    return rejectWithValue(error.response?.data || { message: errorMessage });
};

export const getProducts = createAsyncThunk(
    'products/get',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(BASE_URL + '/allproduct', {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            return { products: response.data.products, deleteStatus: response.data.deleteStatus };
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const getDetailsProducts = createAsyncThunk(
    'products/getDetailsProducts',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/getProduct/${id}`, {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            dispatch(getProducts());
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const addProduct = createAsyncThunk(
    'products/add',
    async (data, { dispatch, rejectWithValue }) => {
        try {
            // console.log("data", data);
            const response = await axiosInstance.post(BASE_URL + '/addNewProduct', data, {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            dispatch(setAlert({ text: response.data.message, color: 'success' }));
            dispatch(getProducts());
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const deleteProduct = createAsyncThunk(
    'products/delete',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`${BASE_URL}/deleteProduct/${id}`, {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            dispatch(setAlert({ text: response.data.message, color: 'success' }));
            dispatch(getProducts());
            return id;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const editProduct = createAsyncThunk(
    'products/edit',
    async (data, { dispatch, rejectWithValue }) => {
        const { id, ...rest } = data;
        try {
            const response = await axiosInstance.put(`${BASE_URL}/updateProduct/${id}`, rest, {
                headers: {
                    Authorization: `Bearer ${Cookies.get("accessToken")}`
                }
            });
            dispatch(setAlert({ text: response.data.message, color: 'success' }));
            dispatch(getProducts());
            return response.data.products;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

const productsSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getProducts.pending, (state) => {
                state.loading = true;
                state.message = 'Fetching products...';
            })
            .addCase(getProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = action.payload?.message || 'Products fetched successfully';
                state.products = action.payload.products;
                state.deleteStatus = action.payload.deleteStatus;
            })
            .addCase(getProducts.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to fetch products';
            })
            .addCase(getDetailsProducts.pending, (state) => {
                state.loading = true;
                state.message = 'Fetching detailed products...';
            })
            .addCase(getDetailsProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = action.payload?.message || 'Detailed products fetched successfully';
                state.products = Array.isArray(action.payload.data) ? action.payload.data : [];
            })
            .addCase(getDetailsProducts.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to fetch detailed products';
            })
            .addCase(addProduct.pending, (state) => {
                state.loading = true;
                state.message = 'Adding product...';
            })
            .addCase(addProduct.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                if (Array.isArray(state.products)) {
                    state.products.push(action.payload.data);
                } else {
                    state.products = [action.payload.data];
                }
                state.message = action.payload?.message || 'Product added successfully';
            })
            .addCase(addProduct.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to add product';
            })
            .addCase(deleteProduct.pending, (state) => {
                state.loading = true;
                state.message = 'Deleting product...';
            })
            .addCase(deleteProduct.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                if (Array.isArray(state.products)) {
                    state.products = state.products.filter((product) => product._id !== action.payload);
                }
                state.message = action.payload?.message || 'Product deleted successfully';
            })
            .addCase(deleteProduct.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to delete product';
            })
            .addCase(editProduct.pending, (state) => {
                state.loading = true;
                state.message = 'Updating product...';
            })
            .addCase(editProduct.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                if (Array.isArray(state.products)) {
                    state.products = state.products.map((v) =>
                        v._id === action.payload._id ? action.payload : v
                    );
                }
                state.message = action.payload?.message || 'Product updated successfully';
            })
            .addCase(editProduct.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to update product';
            });
    }
});

export default productsSlice.reducer;